import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mealsAPI } from '../../services/meals.api';
import {
  fetchSuggestionsStart,
  fetchSuggestionsSuccess,
  fetchSuggestionsFailure,
  updateFilters,
  skipMeal,
  toggleFavorite,
  addToHistory,
  selectSuggestions,
  selectFilters,
} from '../../store/slices/mealsSlice';
import { selectPreferences, selectUser } from '../../store/slices/authSlice';

// Material UI imports
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Fade,
  CircularProgress,
  useTheme,
  alpha,
  Skeleton,
  ButtonBase,
  Avatar,
  useMediaQuery
} from '@mui/material';

// Icons
import {
  Favorite,
  FavoriteBorder,
  SkipNext,
  Share,
  RestaurantMenu,
  AccessTime,
  FoodBank,
  LocalDining,
  TrendingUp,
  EmojiEvents,
  Restaurant,
  Spa,
  Fastfood
} from '@mui/icons-material';

const MealSuggestions = () => {
  const dispatch = useDispatch();
  const suggestions = useSelector(selectSuggestions);
  const filters = useSelector(selectFilters);
  const preferences = useSelector(selectPreferences);
  const user = useSelector(selectUser);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Loading and animation states
  const [loading, setLoading] = useState(false);
  const [highlightedMeal, setHighlightedMeal] = useState(null);
  
  // Mock meal suggestion data for development
  const mockMealSuggestions = [
    {
      id: '1',
      name: 'Masala Dosa',
      description: 'South Indian crispy crepe filled with spiced potatoes',
      imageUrl: 'https://www.vegrecipesofindia.com/wp-content/uploads/2021/07/masala-dosa-1.jpg',
      mealType: 'breakfast',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian', 'south-indian'],
      preparationTime: 30,
      calories: 350,
      isFavorite: false
    },
    {
      id: '2',
      name: 'Chole Bhature',
      description: 'Spicy chickpea curry served with deep-fried bread',
      imageUrl: 'https://static.toiimg.com/thumb/53314156.cms?width=1200&height=900',
      mealType: 'lunch',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian', 'north-indian'],
      preparationTime: 45,
      calories: 550,
      isFavorite: true
    },
    {
      id: '3',
      name: 'Butter Chicken',
      description: 'Creamy tomato curry with tender chicken pieces',
      imageUrl: 'https://cafedelites.com/wp-content/uploads/2019/01/Butter-Chicken-IMAGE-27.jpg',
      mealType: 'dinner',
      dietaryType: ['non-vegetarian'],
      cuisineStyle: ['indian', 'north-indian'],
      preparationTime: 50,
      calories: 650,
      isFavorite: false
    },
    {
      id: '4',
      name: 'Paneer Tikka',
      description: 'Grilled cottage cheese cubes marinated in spices',
      imageUrl: 'https://www.indianhealthyrecipes.com/wp-content/uploads/2022/06/paneer-tikka-recipe.jpg',
      mealType: 'dinner',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian'],
      preparationTime: 35,
      calories: 450,
      isFavorite: false
    },
    {
      id: '5',
      name: 'Uttapam',
      description: 'South Indian savory pancake with vegetables',
      imageUrl: 'https://www.indianhealthyrecipes.com/wp-content/uploads/2022/04/uttapam-recipe.jpg',
      mealType: 'breakfast',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian', 'south-indian'],
      preparationTime: 25,
      calories: 300,
      isFavorite: true
    },
    {
      id: '6',
      name: 'Samosa',
      description: 'Fried pastry with spiced potato filling',
      imageUrl: 'https://static.toiimg.com/thumb/61050397.cms?imgsize=246859&width=800&height=800',
      mealType: 'snack',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian'],
      preparationTime: 40,
      calories: 250,
      isFavorite: false
    }
  ];

  // Fetch suggestions when filters change
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        dispatch(fetchSuggestionsStart());
        
        try {
          // Try to fetch from API
          const response = await mealsAPI.getSuggestions(filters);
          dispatch(fetchSuggestionsSuccess(response.data));
        } catch (apiError) {
          console.log('Using mock data for meal suggestions');
          // Use mock data if API fails
          setTimeout(() => {
            // Filter mock data based on selected filters
            let filteredMeals = [...mockMealSuggestions];
            
            if (filters.mealType) {
              filteredMeals = filteredMeals.filter(meal => 
                meal.mealType.toLowerCase() === filters.mealType.toLowerCase()
              );
            }
            
            if (filters.dietaryType && filters.dietaryType.length > 0) {
              filteredMeals = filteredMeals.filter(meal => 
                filters.dietaryType.some(type => meal.dietaryType.includes(type.toLowerCase()))
              );
            }
            
            dispatch(fetchSuggestionsSuccess(filteredMeals));
          }, 800); // Simulate network delay
        }
        
        setLoading(false);
      } catch (error) {
        dispatch(fetchSuggestionsFailure(error.message));
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [dispatch, filters]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    dispatch(updateFilters({ [filterType]: value }));
  };
  
  const handleMealTypeChange = (event, newMealType) => {
    if (newMealType !== null) {
      dispatch(updateFilters({ mealType: newMealType }));
    }
  };
  
  const handleDietaryTypeChange = (event, newDietaryType) => {
    if (newDietaryType !== null) {
      dispatch(updateFilters({ dietaryType: newDietaryType }));
    }
  };

  // Handle meal actions
  const handleSkip = (mealId) => {
    dispatch(skipMeal(mealId));
  };

  const handleFavorite = (mealId) => {
    dispatch(toggleFavorite(mealId));
  };

  const handleSelect = async (meal) => {
    try {
      setLoading(true);
      setHighlightedMeal(meal.id);
      await mealsAPI.logMeal(meal);
      dispatch(addToHistory(meal));
      // Fetch new suggestions after logging a meal
      const response = await mealsAPI.getSuggestions(filters);
      dispatch(fetchSuggestionsSuccess(response.data));
      setLoading(false);
      setHighlightedMeal(null);
    } catch (error) {
      console.error('Error logging meal:', error);
      setLoading(false);
      setHighlightedMeal(null);
    }
  };

  const handleShare = async (meal) => {
    try {
      await mealsAPI.shareMealPlan({
        mealId: meal.id,
        timestamp: new Date().toISOString(),
      });
      // Replace alert with a better notification later
      alert('Meal plan shared successfully!');
    } catch (error) {
      console.error('Error sharing meal:', error);
    }
  };

  // Function to generate a random image URL (for demo purposes)
  const getRandomImageUrl = (mealId) => {
    const imageTypes = ['indian', 'italian', 'asian', 'breakfast', 'lunch', 'dinner'];
    const imageType = imageTypes[mealId % imageTypes.length];
    return `https://source.unsplash.com/random/400x300/?food,${imageType},meal`;
  };
  
  // Function to get a color for a tag
  const getTagColor = (tag) => {
    const tagMap = {
      'vegetarian': 'success',
      'vegan': 'success',
      'non-vegetarian': 'error',
      'gluten-free': 'info',
      'dairy-free': 'warning',
      'healthy': 'primary',
      'spicy': 'error',
      'sweet': 'secondary'
    };
    return tagMap[tag.toLowerCase()] || 'default';
  };

  // Function to get an icon based on dietary preference
  const getDietaryIcon = (type) => {
    switch(type.toLowerCase()) {
      case 'veg':
      case 'vegetarian':
        return <Spa fontSize="small" sx={{ mr: 0.5 }} />;
      case 'non-veg':
      case 'non-vegetarian':
        return <Fastfood fontSize="small" sx={{ mr: 0.5 }} />;
      default:
        return <LocalDining fontSize="small" sx={{ mr: 0.5 }} />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" fontWeight="bold" color="primary.main" gutterBottom>
          What shall we cook today?
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </Typography>
      </Box>
      
      {/* Filters Section */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 5, 
          borderRadius: 2,
          background: theme => `linear-gradient(120deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={isSmallScreen ? 2 : 0}>
              <LocalDining fontSize="large" color="primary" sx={{ mr: 1.5 }} />
              <Typography variant="h5" fontWeight="bold">
                Meal Filters
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isSmallScreen ? 'center' : 'flex-start', mb: isSmallScreen ? 2 : 0 }}>
              <Typography variant="subtitle1" fontWeight="medium" color="text.secondary" gutterBottom>
                Meal Type
              </Typography>
              <ToggleButtonGroup
                value={filters.mealType}
                exclusive
                onChange={handleMealTypeChange}
                aria-label="meal type"
                size="medium"
                color="primary"
                sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
              >
                {preferences && preferences.mealTypes && preferences.mealTypes.map((type) => {
                  // Handle both string values and object values with name property
                  const typeValue = typeof type === 'string' ? type : type?.value || type?.name;
                  const typeName = typeof type === 'string' ? type : type?.name || type?.value;
                  
                  if (!typeValue) return null; // Skip rendering if no valid value
                  
                  return (
                    <ToggleButton key={typeValue} value={typeValue} aria-label={typeName} sx={{ borderRadius: '16px', m: 0.5, textTransform: 'capitalize' }}>
                      {typeValue === 'breakfast' && <AccessTime fontSize="small" sx={{ mr: 1 }} />}
                      {typeValue === 'lunch' && <FoodBank fontSize="small" sx={{ mr: 1 }} />}
                      {typeValue === 'dinner' && <Restaurant fontSize="small" sx={{ mr: 1 }} />}
                      {typeName}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isSmallScreen ? 'center' : 'flex-start' }}>
              <Typography variant="subtitle1" fontWeight="medium" color="text.secondary" gutterBottom>
                Dietary Preference
              </Typography>
              <ToggleButtonGroup
                value={filters.dietaryType}
                onChange={handleDietaryTypeChange}
                aria-label="dietary preference"
                size="medium"
                color="primary"
                sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
              >
                {preferences && preferences.dietaryType && preferences.dietaryType.map((type) => {
                  // Handle both string values and object values with name property
                  const typeValue = typeof type === 'string' ? type : type?.value || type?.name;
                  const typeName = typeof type === 'string' ? type : type?.name || type?.value;
                  
                  if (!typeValue) return null; // Skip rendering if no valid value
                  
                  return (
                    <ToggleButton key={typeValue} value={typeValue} aria-label={typeName} sx={{ borderRadius: '16px', m: 0.5, textTransform: 'capitalize' }}>
                      {typeValue === 'vegetarian' && getDietaryIcon('veg')}
                      {typeValue === 'non-vegetarian' && getDietaryIcon('non-veg')}
                      {typeName}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Meal Suggestions Heading */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box display="flex" alignItems="center">
          <RestaurantMenu fontSize="large" color="primary" sx={{ mr: 1.5 }} />
          <Typography variant="h4" fontWeight="bold">
            Meal Suggestions
          </Typography>
        </Box>
        
        <Chip 
          icon={<TrendingUp />} 
          label="Personalized for you" 
          color="secondary" 
          variant="outlined" 
          sx={{ fontWeight: 'medium' }}
        />
      </Box>
      
      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress color="primary" size={60} />
        </Box>
      )}

      {/* Meal Suggestions Grid */}
      {!loading && (
        <Grid container spacing={3}>
          {suggestions && suggestions.length > 0 ? suggestions.map((meal) => (
            <Grid item xs={12} sm={6} md={4} key={meal.id}>
              <Fade in={true} style={{ transitionDelay: `${(meal.id % 5) * 100}ms` }}>
                <Card 
                  elevation={highlightedMeal === meal.id ? 8 : 1} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 20px -10px rgba(0,0,0,0.2)'
                    },
                    position: 'relative',
                    overflow: 'visible',
                    borderRadius: 2
                  }}
                >
                  {meal.trending && (
                    <Chip 
                      icon={<EmojiEvents fontSize="small" />}
                      label="Trending" 
                      color="secondary" 
                      size="small" 
                      sx={{ 
                        position: 'absolute', 
                        top: -12, 
                        right: 20, 
                        zIndex: 9,
                        fontWeight: 'bold' 
                      }} 
                    />
                  )}
                  <CardMedia
                    component="img"
                    height="180"
                    image={meal.imageUrl || getRandomImageUrl(meal.id)}
                    alt={meal.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Typography gutterBottom variant="h5" component="h2" fontWeight="bold">
                      {meal.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {meal.description || `A delicious dish perfect for your ${filters.mealType || 'meal'}.`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {meal.tags && meal.tags.length > 0 ? (
                        meal.tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            color={getTagColor(tag)}
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        ))
                      ) : (
                        // Default tags based on meal properties if no tags exist
                        <>
                          {meal.dietaryType && meal.dietaryType.map((type) => (
                            <Chip
                              key={type}
                              label={type}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          ))}
                          {meal.cuisineStyle && meal.cuisineStyle.length > 0 && (
                            <Chip
                              key={meal.cuisineStyle[0]}
                              label={meal.cuisineStyle[0]}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          )}
                        </>
                      )}
                    </Box>
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      startIcon={<RestaurantMenu />}
                      onClick={() => handleSelect(meal)}
                      disabled={loading}
                    >
                      Cook This
                    </Button>
                    <Box>
                      <Tooltip title="Skip" arrow>
                        <IconButton onClick={() => handleSkip(meal.id)} color="default">
                          <SkipNext />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={meal.isFavorite ? "Remove from favorites" : "Add to favorites"} arrow>
                        <IconButton 
                          onClick={() => handleFavorite(meal.id)} 
                          color={meal.isFavorite ? "error" : "default"}
                        >
                          {meal.isFavorite ? <Favorite /> : <FavoriteBorder />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Share" arrow>
                        <IconButton onClick={() => handleShare(meal)} color="primary">
                          <Share />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardActions>
                </Card>
              </Fade>
            </Grid>
          )) : !loading && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No meal suggestions found. Try adjusting your filters.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RestaurantMenu />}
                  onClick={() => dispatch(updateFilters({ mealType: null, dietaryType: [] }))}
                  sx={{ mt: 2 }}
                >
                  Clear Filters
                </Button>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default MealSuggestions;
